import React, { useState, useEffect, useRef, useCallback } from "react";
import { isEmpty, get } from "lodash";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Spacer, Tabs, Loading, Box } from "orcs-design-system";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import { TAGS_UI_COMPONENTS } from "src/consts/tags";
import TagsEditorContainer from "src/components/TagsEditor";
import useHandleEntityTagClick from "src/hooks/useHandleEntityTagClick";
import { useTagsTabs } from "../hooks/useTagsTabs";
import { useHandleNodeToggle } from "../hooks/useHandleNodeToggle";
import { useTagsHierarchyViewer } from "../context";
import { removeTag, updateTag } from "../actions/tagsActions";
import { TAGS_VIEWER_HEIGHT } from "../consts";
import TagsTreeView from "./TagsTreeView";

const TagsHierarchyViewerContainer = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const TagsHierarchyViewer = ({ maxHeight = 350 }) => {
  const [toggledNodes, setToggledNodes] = useState({});
  const [isTabChanging, setIsTabChanging] = useState(false);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const previousOffsetRef = useRef(null);
  const {
    selectedTabType,
    loading,
    pagination,
    entityId,
    entityName,
    entityType,
    tags,
    editMode,
    setEditMode,
    dispatch,
  } = useTagsHierarchyViewer();
  const { tabsList, treeData } = useTagsTabs(toggledNodes);

  const workspace = useWorkspace();
  const workspaceFeatureFlags = get(workspace, "config.featureFlags", {});
  const onEntityTagClick = useHandleEntityTagClick();

  // Callback handlers for tag updates and deletions
  const handleTagRemoved = useCallback(
    (tagId) => {
      dispatch(removeTag(tagId));
    },
    [dispatch]
  );

  const handleTagUpdated = useCallback(
    (updateData) => {
      dispatch(updateTag(updateData));
    },
    [dispatch]
  );

  const handleEditorFinish = useCallback(() => {
    setEditMode(false);
  }, [setEditMode]);

  const { handleNodeToggled, loadingNodeIds } =
    useHandleNodeToggle(setToggledNodes);

  // Reset toggled nodes and mark tab as changing when tab changes
  useEffect(() => {
    setToggledNodes({});
    setIsTabChanging(true);
  }, [selectedTabType]);

  // Track when pagination offset changes (page navigation)
  useEffect(() => {
    const currentOffset = pagination?.offset;
    if (
      previousOffsetRef.current !== null &&
      previousOffsetRef.current !== currentOffset
    ) {
      // Offset changed, reset toggled nodes and mark pagination as loading
      setToggledNodes({});
      setIsPaginationLoading(true);
    }
    previousOffsetRef.current = currentOffset;
  }, [pagination?.offset]);

  // Clear pagination loading and tab changing when data loads successfully
  useEffect(() => {
    if (!loading && !isEmpty(treeData)) {
      setIsTabChanging(false);
      setIsPaginationLoading(false);
    }
  }, [loading, treeData]);

  if (isEmpty(tabsList)) {
    return null;
  }

  const showLoading =
    loading || isTabChanging || isPaginationLoading || isEmpty(treeData);

  return (
    <TagsHierarchyViewerContainer>
      <Spacer mb="s">
        <Tabs tabsList={tabsList} />
        {showLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={TAGS_VIEWER_HEIGHT}
          >
            <Loading centered primary />
          </Box>
        ) : editMode ? (
          <Box height={TAGS_VIEWER_HEIGHT} overflow="auto">
            <TagsEditorContainer
              entityId={entityId}
              entityName={entityName}
              entityType={entityType}
              tagTypesFilter={selectedTabType ? [selectedTabType] : []}
              editOnly={true}
              manageEntityTags={false}
              presetEntityTags={tags}
              tagsVisibleIn={TAGS_UI_COMPONENTS.DIRECTORY}
              featureFlags={workspaceFeatureFlags}
              onEntityTagClick={onEntityTagClick}
              mt="0"
              menuPlacement="bottom"
              hideDropdown={true}
              onTagRemoved={handleTagRemoved}
              onTagUpdated={handleTagUpdated}
              comments={{}}
              onFinishEdit={handleEditorFinish}
            />
          </Box>
        ) : (
          <TagsTreeView
            treeData={treeData}
            maxHeight={maxHeight}
            onNodeToggled={handleNodeToggled}
            loadingNodeIds={loadingNodeIds}
          />
        )}
      </Spacer>
    </TagsHierarchyViewerContainer>
  );
};

TagsHierarchyViewer.propTypes = {
  maxHeight: PropTypes.number,
};

export default TagsHierarchyViewer;
